// The Calendar's event modals (CALENDAR-C1 §3; quick add and edit stay on the page): the quick
// add / edit / duplicate form, the event's details (attendees and their answers, RSVP, Edit,
// "Open meeting" for a meeting's event, its history count), the recurring scope question asked
// before every save or delete of a series ("This event · This and following · All events"),
// and the delete confirmation of a single event.

import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  CalendarDot,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  QuickAddDialog,
  type TagChipColor,
  Textarea,
  toast,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  CopyIcon,
  HistoryIcon,
  LinkIcon,
  MapPinIcon,
  PencilIcon,
  RepeatIcon,
  Trash2Icon,
  UsersIcon,
} from "lucide-react";
import { useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { CalendarEventDetail, CalendarOccurrence, CalendarView } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { peopleQuery } from "../queries";
import { Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import {
  type AttendeeInput,
  addDays,
  attendeeInput,
  browserZone,
  DEFAULT_EVENT_MINUTES,
  dateIn,
  dayLabel,
  type EditScope,
  fromZonedInput,
  localInput,
  moveBody,
  type NewTimes,
  type RepeatKind,
  repeatKindOf,
  repeatRule,
  sameSet,
  timeRange,
  versionFor,
  ymd,
  zonedInput,
  zonedMidnight,
} from "./calendar-model";
import { calendarKeys, eventQuery } from "./calendar-queries";

export type { EditScope };

type Visibility = "default" | "details" | "busy" | "hidden";

// Writes ----------------------------------------------------------------------------------------

/** The event writes, each refreshing the calendar's reads and saying what happened. */
export function useEventWrites(api: Api, co: string) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: calendarKeys.root(co) });
  /** The versions read for an occurrence (cached when fresh) for a write's conflict check. */
  const readOf = (o: CalendarOccurrence) =>
    qc.fetchQuery(eventQuery(api, co, o.event_id, o.recurring || o.series_id ? slotOf(o) : null));
  const conflictOr = (e: unknown, fallback: string) =>
    e instanceof ApiError && e.status === 409
      ? "Changed elsewhere; refreshed"
      : problemText(e, fallback);
  const remove = useMutation({
    mutationFn: async (v: { o: CalendarOccurrence; scope?: EditScope }) => {
      const read = await readOf(v.o);
      return api.client.calendarEvents.delete({
        params: { co, id: v.o.event_id },
        query: {
          ...(v.scope ? { scope: v.scope, occurrence_start: slotOf(v.o) } : {}),
          version: versionFor(v.scope ?? null, read),
        },
      });
    },
    onSuccess: () => {
      void refresh();
      toast.add({ title: "Event deleted", type: "success" });
    },
    onError: (e) => {
      void refresh();
      toast.add({ title: conflictOr(e, "The event could not be deleted."), type: "error" });
    },
  });
  const rsvp = useMutation({
    mutationFn: (v: {
      id: string;
      response: "accepted" | "declined" | "tentative";
      scope?: "this" | "all";
      at?: string | null;
    }) =>
      api.client.calendarEvents.rsvp({
        params: { co, id: v.id },
        body: {
          response: v.response,
          ...(v.scope ? { scope: v.scope } : {}),
          ...(v.scope === "this" && v.at ? { occurrence_start: v.at } : {}),
        },
      }),
    onSuccess: (_d, v) => {
      void refresh();
      toast.add({ title: RSVP_DONE[v.response], type: "success" });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "Your answer could not be saved."), type: "error" }),
  });
  // A drag (move or resize): the new times, the scope on a series, the version read for the
  // conflict check. A 409 means someone changed it meanwhile: refresh and say so.
  const move = useMutation({
    mutationFn: async (v: { o: CalendarOccurrence; times: NewTimes; scope: EditScope | null }) => {
      const read = await readOf(v.o);
      return api.client.calendarEvents.update({
        params: { co, id: v.o.event_id },
        body: moveBody(v.o, v.times, v.scope, read),
      });
    },
    onSuccess: () => {
      void refresh();
      toast.add({ title: "Event moved", type: "success" });
    },
    onError: (e) => {
      void refresh();
      toast.add({ title: conflictOr(e, "The event could not be moved."), type: "error" });
    },
  });
  return { remove, rsvp, move, refresh };
}

const RSVP_DONE = { accepted: "Accepted", declined: "Declined", tentative: "Answered maybe" };

/** The slot of an occurrence of a series (what `scope` + `occurrence_start` name). */
export const slotOf = (o: Pick<CalendarOccurrence, "original_start_at" | "starts_at">) =>
  o.original_start_at ?? o.starts_at;

// Scope -----------------------------------------------------------------------------------------

/** "This event · This and following · All events": asked before a series is saved or deleted. */
export function ScopeDialog({
  verb,
  onPick,
  onClose,
}: {
  verb: "Save" | "Delete";
  onPick: (scope: EditScope) => void;
  onClose: () => void;
}) {
  const options: [EditScope, string][] = [
    ["this", "This event"],
    ["following", "This and following"],
    ["all", "All events"],
  ];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[380px]" data-slot="scope-dialog">
        <DialogHeader>
          <DialogTitle>
            {verb === "Save" ? "Change a repeating event" : "Delete a repeating event"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-2">
          {options.map(([scope, label]) => (
            <Button
              key={scope}
              variant={verb === "Delete" && scope === "all" ? "destructive" : "secondary"}
              className="justify-start"
              onClick={() => onPick(scope)}
            >
              {label}
            </Button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Deleting a single event: one confirmation. */
export function ConfirmDelete({
  title,
  onConfirm,
  onClose,
}: {
  title: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[380px]">
        <DialogHeader>
          <DialogTitle>Delete {title}?</DialogTitle>
          <DialogDescription>Attendees no longer see it.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// People ----------------------------------------------------------------------------------------

export type PersonOption = { id: string; name: string; kind: string };

/** The company's people (the directory a member may read), active only. */
export function usePeople(api: Api, co: string): PersonOption[] {
  const q = useInfiniteQuery({ ...peopleQuery(api, co), retry: false });
  return useMemo(
    () =>
      (q.data?.pages ?? [])
        .flatMap((p) => p.data)
        .filter((m) => m.status === "active")
        .map((m) => ({ id: m.id, name: m.display_name, kind: m.kind }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [q.data],
  );
}

function AttendeePicker({
  people,
  value,
  onChange,
  emails,
  emailValue,
  onEmailsChange,
  exclude,
}: {
  people: PersonOption[];
  value: string[];
  onChange: (ids: string[]) => void;
  /** Outside guests already on the event (kept unless unticked). */
  emails: readonly string[];
  emailValue: string[];
  onEmailsChange: (emails: string[]) => void;
  exclude: string | null;
}) {
  const [q, setQ] = useState("");
  const id = useId();
  const match = (s: string) => !q.trim() || s.toLowerCase().includes(q.trim().toLowerCase());
  const shown = people.filter((p) => p.id !== exclude && match(p.name));
  const shownEmails = emails.filter(match);
  const count = value.filter((v) => v !== exclude).length + emailValue.length;
  return (
    <fieldset className="grid gap-1.5" data-slot="attendee-picker">
      <legend className="mb-1.5 text-sm font-medium text-foreground">
        Attendees{count ? <span className="ml-1 text-faint">{count}</span> : null}
      </legend>
      <Input
        id={`${id}-q`}
        aria-label="Find people"
        placeholder="Find people"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="grid max-h-36 gap-1 overflow-auto rounded-md border border-subtle p-2">
        {shownEmails.map((email) => (
          // biome-ignore lint/a11y/noLabelWithoutControl: the checkbox is inside
          <label key={`e-${email}`} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={emailValue.includes(email)}
              onCheckedChange={(c) =>
                onEmailsChange(c ? [...emailValue, email] : emailValue.filter((x) => x !== email))
              }
            />
            <span className="truncate">{email}</span>
            <Badge variant="outline">Guest</Badge>
          </label>
        ))}
        {shown.map((p) => (
          // biome-ignore lint/a11y/noLabelWithoutControl: the checkbox is inside
          <label key={p.id} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={value.includes(p.id)}
              onCheckedChange={(c) =>
                onChange(c ? [...value, p.id] : value.filter((x) => x !== p.id))
              }
            />
            <span className="truncate">{p.name}</span>
            {p.kind === "digital_employee" ? <Badge variant="outline">DE</Badge> : null}
          </label>
        ))}
        {!shown.length && !shownEmails.length ? (
          <p className="text-xs text-faint">Nobody matches.</p>
        ) : null}
      </div>
    </fieldset>
  );
}

// Quick add / edit ------------------------------------------------------------------------------

export type EventFormSeed =
  | {
      mode: "create";
      day: string;
      hour: number | null;
      minute?: number;
      /** Minutes long (default an hour); a free gap's pick ends inside the gap. */
      length?: number;
    }
  | { mode: "edit"; occurrence: CalendarOccurrence; detail: CalendarEventDetail }
  | { mode: "duplicate"; detail: CalendarEventDetail };

const VISIBILITY: [Visibility, string][] = [
  ["default", "Default"],
  ["details", "Details"],
  ["busy", "Busy"],
  ["hidden", "Hidden"],
];

const REPEATS: [RepeatKind, string][] = [
  ["none", "Does not repeat"],
  ["daily", "Daily"],
  ["weekly", "Weekly on this day"],
  ["monthly", "Monthly"],
  ["custom", "Custom (RRULE)"],
];

/** The form's starting state for a seed (exported for the request-shape tests). */
export function formStateOf(
  seed: EventFormSeed,
  calendars: readonly CalendarView[],
  myCalendar: string | null,
) {
  const writable = calendars.filter((c) => c.access === "write");
  const fallback = writable.find((c) => c.id === myCalendar)?.id ?? writable[0]?.id ?? "";
  if (seed.mode === "create") {
    const [y, m, day] = seed.day.split("-").map(Number);
    const start = new Date(y ?? 1970, (m ?? 1) - 1, day ?? 1, seed.hour ?? 9, seed.minute ?? 0);
    const end = new Date(start.getTime() + (seed.length ?? DEFAULT_EVENT_MINUTES) * 60_000);
    return {
      title: "",
      calendarId: fallback,
      allDay: false,
      start: localInput(start),
      end: localInput(end),
      startDay: seed.day,
      endDay: seed.day,
      timezone: browserZone(),
      repeat: "none" as RepeatKind,
      custom: "",
      attendees: [] as string[],
      emails: [] as string[],
      location: "",
      visibility: "default" as Visibility,
      description: "",
    };
  }
  const d = seed.detail;
  const starts = seed.mode === "edit" ? seed.occurrence.starts_at : d.starts_at;
  const ends = seed.mode === "edit" ? seed.occurrence.ends_at : d.ends_at;
  const rule = d.rrule ?? d.series_rrule;
  // An event's times and dates (and its repeat weekday) are read and written on its own zone's
  // clock, and it keeps that zone; the browser's zone is for new events only.
  const timezone = d.timezone || browserZone();
  const startDay = dateIn(starts, timezone);
  const lastDay = d.all_day
    ? addDays(dateIn(ends, timezone), -1)
    : dateIn(new Date(new Date(ends).getTime() - 1).toISOString(), timezone);
  return {
    title: d.title,
    // An edit keeps its calendar even when it is not in the overlay list (never a silent move);
    // a duplicate goes to it only when it is writable here.
    calendarId:
      seed.mode === "edit" || writable.some((c) => c.id === d.calendar_id)
        ? d.calendar_id
        : fallback,
    allDay: d.all_day,
    start: zonedInput(starts, timezone),
    end: zonedInput(ends, timezone),
    startDay,
    endDay: lastDay < startDay ? startDay : lastDay,
    timezone,
    repeat: seed.mode === "duplicate" ? ("none" as RepeatKind) : repeatKindOf(rule),
    custom: seed.mode === "duplicate" ? "" : (rule ?? ""),
    attendees: d.attendees.flatMap((a) => (a.membership_id ? [a.membership_id] : [])),
    emails: d.attendees.flatMap((a) => (!a.membership_id && a.email ? [a.email] : [])),
    location: d.location ?? "",
    visibility: d.visibility as Visibility,
    description: d.description ?? "",
  };
}

type FormState = ReturnType<typeof formStateOf>;

/**
 * The times a form state names and the zone they were built in (the form's zone: the event's
 * own on an edit, the browser's for a new one): all-day dates at midnight there, timed ones and
 * the repeat weekday on its clock. Null if bad.
 */
function timesOf(f: FormState): { starts_at: string; ends_at: string; timezone: string } | null {
  if (f.allDay) {
    if (!f.startDay || !f.endDay || f.endDay < f.startDay) return null;
    return {
      starts_at: zonedMidnight(f.startDay, f.timezone),
      ends_at: zonedMidnight(addDays(f.endDay, 1), f.timezone),
      timezone: f.timezone,
    };
  }
  const s = fromZonedInput(f.start, f.timezone);
  const e = fromZonedInput(f.end, f.timezone);
  if (!s || !e || e <= s) return null;
  return { starts_at: s, ends_at: e, timezone: f.timezone };
}

/**
 * An edit's PATCH body: only what the person changed, so a title edit never moves, re-times or
 * re-invites. On a series the slot is always sent (the API moves the series by the occurrence's
 * offset for "All events" too), the rule only when the repeat changed and never for one event.
 */
export function editBody(
  o: Pick<CalendarOccurrence, "original_start_at" | "starts_at">,
  detail: Pick<CalendarEventDetail, "calendar_id" | "version" | "series_version" | "attendees">,
  before: FormState,
  f: FormState,
  scope: EditScope | null,
) {
  const t = timesOf(f);
  const t0 = timesOf(before);
  const body: {
    title?: string;
    location?: string | null;
    description?: string | null;
    visibility?: Visibility;
    all_day?: boolean;
    starts_at?: string;
    ends_at?: string;
    timezone?: string;
    attendees?: AttendeeInput[];
    calendar_id?: string;
    rrule?: string | null;
    scope?: EditScope;
    occurrence_start?: string;
  } = {};
  if (f.title.trim() !== before.title.trim()) body.title = f.title.trim();
  if (f.location.trim() !== before.location.trim()) body.location = f.location.trim() || null;
  if (f.description.trim() !== before.description.trim())
    body.description = f.description.trim() || null;
  if (f.visibility !== before.visibility) body.visibility = f.visibility;
  if (f.allDay !== before.allDay) body.all_day = f.allDay;
  if (
    t &&
    (f.allDay !== before.allDay ||
      t.starts_at !== t0?.starts_at ||
      t.ends_at !== t0?.ends_at ||
      t.timezone !== t0?.timezone)
  )
    Object.assign(body, t);
  if (!sameSet(f.attendees, before.attendees) || !sameSet(f.emails, before.emails))
    body.attendees = attendeeInput(detail.attendees, f.attendees, f.emails);
  if (f.calendarId !== detail.calendar_id) body.calendar_id = f.calendarId;
  const ruleChanged =
    f.repeat !== before.repeat ||
    (f.repeat === "custom" && f.custom.trim() !== before.custom.trim());
  if (scope !== "this" && ruleChanged) {
    body.rrule = repeatRule(f.repeat, f.allDay ? f.startDay : f.start.slice(0, 10), f.custom);
    // The rule's weekday was read in the zone the form works in: say which.
    if (t) body.timezone = t.timezone;
  }
  if (scope) {
    body.scope = scope;
    body.occurrence_start = slotOf(o);
  }
  return { ...body, version: versionFor(scope, detail) };
}

export function EventFormDialog({
  api,
  company,
  calendars,
  myCalendarId,
  myMembershipId,
  seed,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  calendars: readonly CalendarView[];
  myCalendarId: string | null;
  myMembershipId: string | null;
  seed: EventFormSeed;
  onClose: () => void;
}) {
  const co = company.id;
  const id = useId();
  const qc = useQueryClient();
  const people = usePeople(api, co);
  const [before] = useState(() => formStateOf(seed, calendars, myCalendarId));
  const [f, setF] = useState(before);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((s) => ({ ...s, [k]: v }));
  const [asking, setAsking] = useState(false);
  const writable = calendars.filter((c) => c.access === "write");
  const editing = seed.mode === "edit";
  const series =
    editing && (seed.occurrence.recurring || Boolean(seed.detail.rrule || seed.detail.series_id));
  const times = () => timesOf(f);

  const save = useMutation({
    mutationFn: (scope: EditScope | null) => {
      const t = times();
      if (!t) throw new Error("times");
      if (seed.mode === "edit") {
        const o = seed.occurrence;
        return api.client.calendarEvents.update({
          params: { co, id: o.event_id },
          body: editBody(o, seed.detail, before, f, scope),
        });
      }
      return api.client.calendarEvents.create({
        params: { co },
        body: {
          title: f.title.trim(),
          location: f.location.trim() || null,
          description: f.description.trim() || null,
          all_day: f.allDay,
          visibility: f.visibility,
          attendees:
            seed.mode === "duplicate"
              ? attendeeInput(seed.detail.attendees, f.attendees, f.emails)
              : f.attendees.map((membership_id) => ({ membership_id })),
          ...t,
          calendar_id: f.calendarId,
          rrule: repeatRule(f.repeat, f.allDay ? f.startDay : f.start.slice(0, 10), f.custom),
        },
      });
    },
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: calendarKeys.root(co) });
      toast.add({
        title: editing ? "Event saved" : "Event added",
        description: d.title,
        type: "success",
      });
      onClose();
    },
  });

  const valid = Boolean(f.title.trim() && f.calendarId && times());
  const submit = () => {
    if (series) setAsking(true);
    else save.mutate(null);
  };

  return (
    <>
      <QuickAddDialog
        title={editing ? "Edit event" : seed.mode === "duplicate" ? "Duplicate event" : "New event"}
        submitLabel={editing ? "Save" : "Add event"}
        pending={save.isPending}
        canSubmit={valid}
        onSubmit={submit}
        onClose={onClose}
        size="lg"
        slot="event-form"
        error={
          save.isError ? (
            <p role="alert" className="text-sm text-danger-text">
              {problemText(save.error, "The event could not be saved.")}
            </p>
          ) : null
        }
      >
        <Field>
          <FieldLabel htmlFor={`${id}-title`}>Title</FieldLabel>
          <Input
            id={`${id}-title`}
            value={f.title}
            autoFocus
            required
            onChange={(e) => set("title", e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
          <Field>
            <FieldLabel htmlFor={`${id}-cal`}>Calendar</FieldLabel>
            <NativeSelect
              id={`${id}-cal`}
              value={f.calendarId}
              onChange={(e) => set("calendarId", e.target.value)}
              className="w-full"
            >
              {editing && !writable.some((c) => c.id === seed.detail.calendar_id) ? (
                <NativeSelectOption value={seed.detail.calendar_id}>
                  {seed.detail.calendar_name}
                </NativeSelectOption>
              ) : null}
              {writable.map((c) => (
                <NativeSelectOption key={c.id} value={c.id}>
                  {calendarLabel(c, myCalendarId)}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field>
            <span className="flex items-center gap-1">
              <FieldLabel htmlFor={`${id}-vis`}>Visibility</FieldLabel>
              <InfoTip label="About visibility">
                Default follows the calendar. Details opens this event to everyone who sees the
                calendar; Busy shows only a block; Hidden shows nothing to others.
              </InfoTip>
            </span>
            <NativeSelect
              id={`${id}-vis`}
              value={f.visibility}
              onChange={(e) => set("visibility", e.target.value as Visibility)}
              className="w-full"
            >
              {VISIBILITY.map(([v, label]) => (
                <NativeSelectOption key={v} value={v}>
                  {label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
        </div>
        {/* biome-ignore lint/a11y/noLabelWithoutControl: the checkbox is inside */}
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={f.allDay} onCheckedChange={(c) => set("allDay", Boolean(c))} />
          All day
        </label>
        {f.allDay ? (
          <div className="grid grid-cols-2 gap-3">
            <Field>
              <FieldLabel htmlFor={`${id}-sd`}>From</FieldLabel>
              <Input
                id={`${id}-sd`}
                type="date"
                value={f.startDay}
                onChange={(e) => set("startDay", e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={`${id}-ed`}>To</FieldLabel>
              <Input
                id={`${id}-ed`}
                type="date"
                value={f.endDay}
                onChange={(e) => set("endDay", e.target.value)}
              />
            </Field>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Field>
              <FieldLabel htmlFor={`${id}-s`}>Starts</FieldLabel>
              <Input
                id={`${id}-s`}
                type="datetime-local"
                value={f.start}
                onChange={(e) => {
                  const next = e.target.value;
                  // The end keeps the duration when the start moves.
                  const a = fromZonedInput(f.start, f.timezone);
                  const b = fromZonedInput(f.end, f.timezone);
                  const n = fromZonedInput(next, f.timezone);
                  setF((s) => ({
                    ...s,
                    start: next,
                    ...(a && b && n
                      ? {
                          end: zonedInput(
                            new Date(new Date(n).getTime() + (Date.parse(b) - Date.parse(a))),
                            f.timezone,
                          ),
                        }
                      : {}),
                  }));
                }}
              />
            </Field>
            <Field>
              <span className="flex items-center gap-1">
                <FieldLabel htmlFor={`${id}-e`}>Ends</FieldLabel>
                {f.timezone !== browserZone() ? (
                  <InfoTip label="About the time zone">
                    Times are in the event's zone, {f.timezone}; it keeps that zone.
                  </InfoTip>
                ) : null}
              </span>
              <Input
                id={`${id}-e`}
                type="datetime-local"
                value={f.end}
                onChange={(e) => set("end", e.target.value)}
              />
            </Field>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
          <Field>
            <FieldLabel htmlFor={`${id}-rep`}>Repeat</FieldLabel>
            <NativeSelect
              id={`${id}-rep`}
              value={f.repeat}
              onChange={(e) => set("repeat", e.target.value as RepeatKind)}
              className="w-full"
            >
              {REPEATS.map(([v, label]) => (
                <NativeSelectOption key={v} value={v}>
                  {label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          {f.repeat === "custom" ? (
            <Field>
              <FieldLabel htmlFor={`${id}-rule`}>Rule</FieldLabel>
              <Input
                id={`${id}-rule`}
                placeholder="FREQ=WEEKLY;BYDAY=MO,WE"
                value={f.custom}
                onChange={(e) => set("custom", e.target.value)}
              />
            </Field>
          ) : (
            <Field>
              <FieldLabel htmlFor={`${id}-loc`}>Location</FieldLabel>
              <Input
                id={`${id}-loc`}
                value={f.location}
                onChange={(e) => set("location", e.target.value)}
              />
            </Field>
          )}
        </div>
        {f.repeat === "custom" ? (
          <Field>
            <FieldLabel htmlFor={`${id}-loc`}>Location</FieldLabel>
            <Input
              id={`${id}-loc`}
              value={f.location}
              onChange={(e) => set("location", e.target.value)}
            />
          </Field>
        ) : null}
        <AttendeePicker
          people={people}
          value={f.attendees}
          onChange={(v) => set("attendees", v)}
          emails={before.emails}
          emailValue={f.emails}
          onEmailsChange={(v) => set("emails", v)}
          exclude={myMembershipId}
        />
        <Field>
          <FieldLabel htmlFor={`${id}-desc`}>Notes</FieldLabel>
          <Textarea
            id={`${id}-desc`}
            rows={2}
            value={f.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </Field>
      </QuickAddDialog>
      {asking ? (
        <ScopeDialog
          verb="Save"
          onClose={() => setAsking(false)}
          onPick={(scope) => {
            setAsking(false);
            save.mutate(scope);
          }}
        />
      ) : null}
    </>
  );
}

/** A calendar's name in a list: "Mine" for the caller's own. */
export function calendarLabel(c: CalendarView, myCalendarId: string | null): string {
  if (c.id === myCalendarId) return "Mine";
  if (c.kind === "person") return c.owner_name ?? c.name;
  if (c.kind === "team") return c.team_name ?? c.name;
  return c.name;
}

// Details ---------------------------------------------------------------------------------------

const RESPONSE: Record<
  string,
  { label: string; variant: "success" | "danger" | "warning" | "neutral" }
> = {
  accepted: { label: "Going", variant: "success" },
  declined: { label: "Declined", variant: "danger" },
  tentative: { label: "Maybe", variant: "warning" },
  needs_action: { label: "Invited", variant: "neutral" },
};

export function EventDetailDialog({
  api,
  company,
  occurrence: o,
  color,
  onEdit,
  onDuplicate,
  onDelete,
  onCopyLink,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  occurrence: CalendarOccurrence;
  color: TagChipColor;
  onEdit: (detail: CalendarEventDetail) => void;
  onDuplicate: (detail: CalendarEventDetail) => void;
  onDelete: () => void;
  onCopyLink: () => void;
  onClose: () => void;
}) {
  const co = company.id;
  const at = o.recurring || o.series_id ? slotOf(o) : null;
  const q = useQuery({ ...eventQuery(api, co, o.event_id, at), retry: false });
  const writes = useEventWrites(api, co);
  const [rsvpScope, setRsvpScope] = useState<"this" | "all">("this");
  const d = q.data;
  const recurring = Boolean(o.recurring || d?.series_id || d?.rrule);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[520px]" data-slot="event-detail">
        <DialogHeader>
          <span className="flex min-w-0 items-center gap-2 pr-8">
            <CalendarDot color={color} />
            <DialogTitle className="truncate">{d?.title ?? o.title ?? "Event"}</DialogTitle>
          </span>
          <DialogDescription>
            {dayLabel(ymd(new Date(o.starts_at)), "long")} · {timeRange(o)}
            {recurring ? (
              <span className="ml-1.5 inline-flex items-center gap-1">
                · <RepeatIcon aria-hidden="true" className="size-3" /> Repeats
              </span>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        {q.isPending ? (
          <Loading label="Loading the event" rows={3} />
        ) : q.isError || !d ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(q.error, "The event could not be opened.")}
          </p>
        ) : (
          <div className="grid gap-4">
            <dl className="grid grid-cols-[96px_1fr] gap-x-3 gap-y-1.5 text-sm">
              <dt className="text-muted-foreground">Calendar</dt>
              <dd>{d.calendar_name}</dd>
              {d.organizer_name ? (
                <>
                  <dt className="text-muted-foreground">Organizer</dt>
                  <dd>{d.organizer_name}</dd>
                </>
              ) : null}
              {d.location ? (
                <>
                  <dt className="text-muted-foreground">
                    <MapPinIcon aria-hidden="true" className="mr-1 inline size-3.5" />
                    Where
                  </dt>
                  <dd>{d.location}</dd>
                </>
              ) : null}
            </dl>
            {d.description ? (
              <p className="text-sm whitespace-pre-wrap text-secondary">{d.description}</p>
            ) : null}
            {d.attendees.length ? (
              <section aria-label="Attendees">
                <h3 className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                  <UsersIcon aria-hidden="true" className="size-3.5" />
                  {d.attendees.length} {d.attendees.length === 1 ? "attendee" : "attendees"}
                </h3>
                <ul className="m-0 grid list-none gap-1 p-0">
                  {d.attendees.map((a) => {
                    const r = RESPONSE[a.response] ?? RESPONSE.needs_action;
                    return (
                      <li key={a.id} className="flex items-center gap-2 text-sm">
                        <span className="min-w-0 flex-1 truncate">{a.name ?? a.email}</span>
                        {a.member_kind === "digital_employee" ? (
                          <Badge variant="outline">DE</Badge>
                        ) : null}
                        <Badge variant={r?.variant ?? "neutral"}>{r?.label}</Badge>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}
            {d.can_rsvp ? (
              <section aria-label="Your answer" className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-muted-foreground">Going?</span>
                {(
                  [
                    ["accepted", "Yes"],
                    ["tentative", "Maybe"],
                    ["declined", "No"],
                  ] as const
                ).map(([response, label]) => (
                  <Button
                    key={response}
                    size="sm"
                    variant={d.my_response === response ? "default" : "secondary"}
                    aria-pressed={d.my_response === response}
                    disabled={writes.rsvp.isPending}
                    onClick={() =>
                      writes.rsvp.mutate({
                        id: o.event_id,
                        response,
                        ...(recurring ? { scope: rsvpScope, at: slotOf(o) } : {}),
                      })
                    }
                  >
                    {label}
                  </Button>
                ))}
                {recurring ? (
                  <NativeSelect
                    aria-label="Answer for"
                    value={rsvpScope}
                    onChange={(e) => setRsvpScope(e.target.value as "this" | "all")}
                  >
                    <NativeSelectOption value="this">This event</NativeSelectOption>
                    <NativeSelectOption value="all">All events</NativeSelectOption>
                  </NativeSelect>
                ) : null}
              </section>
            ) : null}
            {d.read_only_reason ? (
              <p className="text-xs text-muted-foreground">{d.read_only_reason}</p>
            ) : null}
          </div>
        )}
        <DialogFooter className="flex-wrap sm:justify-between">
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            {d && d.revisions > 0 ? (
              <span className="inline-flex items-center gap-1" data-slot="event-history">
                <HistoryIcon aria-hidden="true" className="size-3.5" />
                Edited {d.revisions} {d.revisions === 1 ? "time" : "times"}
              </span>
            ) : null}
            {d?.source === "meeting" && d.source_ref ? (
              <Link
                to="/c/$slug/kpis/meetings/$meetingId"
                params={{ slug: company.slug, meetingId: d.source_ref }}
                search={keepFocus}
                className="font-medium text-primary-text hover:underline"
              >
                Open meeting
              </Link>
            ) : null}
          </span>
          <span className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={onCopyLink}>
              <LinkIcon data-icon="inline-start" aria-hidden="true" />
              Copy link
            </Button>
            {d?.can_edit ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => onDuplicate(d)}>
                  <CopyIcon data-icon="inline-start" aria-hidden="true" />
                  Duplicate
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Delete event"
                  onClick={onDelete}
                  className="hover:text-danger-text"
                >
                  <Trash2Icon aria-hidden="true" />
                </Button>
                <Button size="sm" onClick={() => onEdit(d)}>
                  <PencilIcon data-icon="inline-start" aria-hidden="true" />
                  Edit
                </Button>
              </>
            ) : null}
          </span>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
